import { faBabyCarriage } from "@fortawesome/pro-regular-svg-icons/faBabyCarriage";
import { faBan } from "@fortawesome/pro-regular-svg-icons/faBan";
import { faUserGraduate } from "@fortawesome/pro-regular-svg-icons/faUserGraduate";
import { faWalking } from "@fortawesome/pro-regular-svg-icons/faWalking";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChartJsHeight, ChartJsOptions } from "assets/appConfig";
import { DashboardPatterns } from 'assets/jss/common-styles';
import * as ChartJs from "chart.js";
import { i18n } from 'i18next';
import { KnowledgeTestDifficultyLevel, KnowledgeTestImportance } from 'models/ApiEnums';
import { DashboardGraphByGroupModel, DashboardKnowledgeIndicatorsDataModel, DashboardKnowledgeIndicatorsDetailColumn, DashboardKnowledgeIndicatorsSummaryColumn } from 'models/ApiModels';
import * as React from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////


///
const KnowledgeTestLevelNameComponent: React.FC<{ value: KnowledgeTestDifficultyLevel; } & WithTranslation> = (props) => {
    return props.t(`KnowledgeTestDifficultyLevel.${KnowledgeTestDifficultyLevel[props.value]}`);
};
export const KnowledgeTestLevelName = withTranslation("sys")(KnowledgeTestLevelNameComponent);

///
const KnowledgeTestImportaneceNameComponent: React.FC<{ value: KnowledgeTestImportance; } & WithTranslation> = (props) => {
    return props.t(`KnowledgeTestImportance.${KnowledgeTestImportance[props.value]}`);
};
export const KnowledgeTestImportanceName = withTranslation("sys")(KnowledgeTestImportaneceNameComponent);

///
export const KnowledgeTestLevelIcon: React.FC<{ currentLevel: KnowledgeTestDifficultyLevel; }> = ({ currentLevel }) => {
    switch (currentLevel) {
        case KnowledgeTestDifficultyLevel.NotInitiate:
            return <FontAwesomeIcon icon={faBan} />;
        case KnowledgeTestDifficultyLevel.Beginner:
            return <FontAwesomeIcon icon={faBabyCarriage} />;
        case KnowledgeTestDifficultyLevel.Intermediate:
            return <FontAwesomeIcon icon={faWalking} />;
        case KnowledgeTestDifficultyLevel.Advanced:
            return <FontAwesomeIcon icon={faUserGraduate} />;
    }
    return null;
};

///
export const KnowledgeTotalColumn: React.FC<{ summaryColumn: DashboardKnowledgeIndicatorsSummaryColumn, data: DashboardKnowledgeIndicatorsDataModel; }> = ({ summaryColumn, data }) => {

    switch (summaryColumn) {
        case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
            return <>{data.knowledgePercent.toFixed(1)}%</>;
        case DashboardKnowledgeIndicatorsSummaryColumn.Result:
            return <>{data.resultPercent.toFixed(1)}%</>;
        case DashboardKnowledgeIndicatorsSummaryColumn.Completed:
            return <>{data?.completedPercent.toFixed(1)}%</>;
        case DashboardKnowledgeIndicatorsSummaryColumn.Revision:
            return <>{data.revisionPercent.toFixed(1)}%</>;
        case DashboardKnowledgeIndicatorsSummaryColumn.Trust:
            return <>{data.trustPercent.toFixed(1)}%</>;
    }

    return null;
};


///
export const KnowlegeDataModelLine: React.FC<{ summaryColumn: DashboardKnowledgeIndicatorsSummaryColumn, data: DashboardKnowledgeIndicatorsDataModel; }> = ({ summaryColumn, data }) => {
    const { t } = useTranslation();
    const prefix = "Views.Dashboards.Components.Commons.KnowlegeDataModelLine";
    switch (summaryColumn) {
        case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
            return (
                <>
                    {t(`${prefix}.Answered`, { count: data.answeredTests })}
                    {
                        data.totalUnanswered + data.totalWrong > 0 &&
                        <>
                            <br />
                            {data.totalUnanswered + data.totalWrong} {t(`${prefix}.ToComplete`)}
                        </>
                    }
                </>
            );

        case DashboardKnowledgeIndicatorsSummaryColumn.Completed:
            return (
                <span>
                    {
                        data.totalAvailableTests > data.activeAnsweredTests
                            ? `${data.totalAvailableTests - data.activeAnsweredTests} ${t(`${prefix}.ToComplete`)}`
                            : t(`${prefix}.AllCompleted`)
                    }
                </span>
            );

        case DashboardKnowledgeIndicatorsSummaryColumn.Result:
            return (
                <>
                    {t(`${prefix}.Answered`, { count: data.answeredTests })}
                </>
            );

        case DashboardKnowledgeIndicatorsSummaryColumn.Revision:
            return data.revisionAnsweredTests > 0
                ? (
                    <>
                        {t(`${prefix}.Revision`, { count: data.revisionAnsweredTests })}
                        <br />
                        {t(`${prefix}.Correct`, { count: data.revisionCorrectTests })}
                    </>
                )
                : null;
    }

    return null;
};


/////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface KnowledgeTestGraphProps {
    tile: DashboardKnowledgeIndicatorsSummaryColumn;
    data: DashboardGraphByGroupModel;
    onBarSelected: (index: number) => void;
}

export const KnowledgeTestGraph: React.FC<KnowledgeTestGraphProps> = (props) => {

    const { tile, data } = props;
    const { t } = useTranslation();
    var ds: number[] = [];
    var labels: string[][] = [];
    var backgroundColors: string[] = [];

    var bc = DashboardPatterns.knowlegdeBars;

    switch (tile) {
        case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
            {
                const l = t("Views.Dashboards.Components.Commons.KnowledgeTestGraph.NoTestAnswered").split('\n');
                ds = [data.f0, data.f1, data.f2, data.f3];
                backgroundColors = [bc.f0.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
                labels = [
                    [l[0], l[1]],
                    [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange1")],
                    [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange2")],
                    [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange3")],
                ];
                break;
            }
        case DashboardKnowledgeIndicatorsSummaryColumn.Result:
        case DashboardKnowledgeIndicatorsSummaryColumn.Trust:
            ds = [data.f1, data.f2, data.f3, data.f4];
            backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
            labels = [["0-24%"], ["25-49%"], ["50-74%"], ["75-100%"]];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Completed:
            ds = [data.f1, data.f2, data.f3, data.f4, data.f5];
            backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
            labels = [["0-24%"], ["25-49%"], ["50-74%"], ["75-99%"], ["100%"]];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Revision:
            backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
            labels = [
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange0")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange1")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange2")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange3")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange4")],
            ];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Engagement:
            {
                const l = t("Views.Dashboards.Components.Commons.KnowledgeTestGraph.NeverAnswered").split('\n');
                const days = t("Glossary.Days").toLowerCase();
                ds = [data.f0, data.f1, data.f2, data.f3, data.f4, data.f5];
                backgroundColors = [bc.f0.backgroundColor, bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
                labels = [
                    [l[0], l[1]],
                    [`${t("Common.Literals.MoreThan")} 30 ${days}`],
                    [`30-15 ${days}`],
                    [`14-8 ${days}`],
                    [t("Views.Dashboards.Common.LastWeek")],
                    [t("Views.Dashboards.Common.AllAnswered")]
                ];
                break;
            }
    }

    let stepWidth = 1;
    let maxValue = Math.max(...ds);
    if (maxValue > 0) {
        if (maxValue % 10 !== 0) maxValue += 10 - maxValue % 10;
        stepWidth = Math.ceil(maxValue / 10);
    }
    else maxValue = null;

    return (
        <div style={{ position: "relative", height: ChartJsHeight, width: "100%" }}>
            <Bar
                data={
                    {
                        labels: labels,
                        datasets: [
                            {
                                label: null,
                                backgroundColor: backgroundColors,
                                data: ds
                            }
                        ]
                    }
                }
                options={
                    {
                        ...ChartJsOptions,
                        plugins: {
                            legend: {
                                display: false
                            },
                            tooltip: {
                                enabled: false
                            }
                        },
                        scales: {
                            x: {
                                display: true,
                                grid: {
                                    display: false
                                },
                            },
                            y: {
                                display: true,
                                grid: {
                                    display: true
                                },
                                suggestedMin: 0,
                                max: maxValue,
                                ticks: {
                                    stepSize: stepWidth
                                }
                            }
                        }
                    }
                }

                getElementsAtEvent={
                    (e) => {
                        if (e.length > 0) {
                            props.onBarSelected((e[0] as any).index);
                        }
                    }
                }
            />
        </div>
    );
};


export const KnowledgeTestHGraph: React.FC<KnowledgeTestGraphProps> = (props) => {
    const { t } = useTranslation();
    const { tile, data } = props;
    var ds: number[] = [];
    var labels: string[][] = [];
    var backgroundColors: string[] = [];
    var bc = DashboardPatterns.knowlegdeBars;

    switch (tile) {
        case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
            ds = [data.f0, data.f1, data.f2, data.f3, data.f4, data.f5];
            backgroundColors = [bc.f0.backgroundColor, bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
            labels = [[t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange0")], ["0-24%"], ["25-49%"], ["50-74%"], ["75-99%"], ["100%"]];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Result:
            ds = [data.f1, data.f2, data.f3, data.f4];
            backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
            labels = [["0-24%"], ["25-49%"], ["50-74%"], ["75-100%"]];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Trust:
            ds = [data.f1, data.f2, data.f3, data.f4];
            backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
            labels = [["0-24%"], ["25-49%"], ["50-74%"], ["75-100%"]];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Completed:
            ds = [data.f1, data.f2, data.f3, data.f4, data.f5];
            backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
            labels = [["0-24%"], ["25-49%"], ["50-74%"], ["75-99%"], ["100%"]];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Revision:
            ds = [data.f0, data.f1, data.f2, data.f3, data.f4];
            backgroundColors = [bc.f0.backgroundColor, bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
            labels = [
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange0")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange1")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange2")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange3")],
                [t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange4")]
            ];
            break;
        case DashboardKnowledgeIndicatorsSummaryColumn.Engagement:
            {
                const days = t("Glossary.Days").toLowerCase();
                ds = [data.f0, data.f1, data.f2, data.f3, data.f4, data.f5];
                backgroundColors = [bc.f0.backgroundColor, bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
                labels = [
                    [t("Views.Dashboards.Components.Commons.KnowledgeTestGraph.NeverAnswered")],
                    [`${t("Common.Literals.MoreThan")} 30 ${days}`],
                    [`30-15 ${days}`],
                    [`14-8 ${days}`],
                    [t("Views.Dashboards.Common.LastWeek")],
                    [t("Views.Dashboards.Common.AllAnswered")]
                ];
            }
            break;
    }

    const total = ds.length == 0 ? 0 : ds.reduce((p, c) => p + c);
    const d = ds.map((v, index) => { return { label: labels[index][0], backgroundColor: backgroundColors[index], data: [total == 0 ? 0 : v / total * 100.0] }; });

    return (
        <div id="graphCanvas" style={{ position: "relative", height: 150, width: "100%" }}>
            <Bar
                data={
                    {
                        labels: [t("Views.Dashboards.Topic.KnowledgeDatabase.DistributionChartLabel")],
                        datasets: d
                    }
                }
                options={
                    {
                        ...ChartJsOptions,
                        indexAxis: "y",
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: true,
                                position: "bottom"
                            },
                            tooltip: {
                                callbacks: {
                                    label: function (item: any) {
                                        return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${ds[item.datasetIndex]})`;
                                    },
                                }
                            }
                        },

                        scales: {
                            x: {
                                display: true,
                                stacked: true,
                                title: {
                                    display: false,
                                },
                                min: 0,
                                max: 100,
                                grid: {
                                    display: false
                                },
                                ticks: {
                                    display: false
                                }
                            },
                            y: {
                                display: false,
                                stacked: true,
                                title: {
                                    display: false,
                                }
                            }
                        }
                    }
                }

                getElementsAtEvent={
                    (e: Array<ChartJs.InteractionItem>, event: React.MouseEvent<HTMLCanvasElement>) => {
                        if (e.length > 0) {
                            const div = document.getElementById("graphCanvas").getBoundingClientRect();
                            const bar = e.filter((p) => event.clientX >= (div.x + (p.element as any).base) && event.clientX <= (div.x + p.element.x));
                            if (bar.length > 0) {
                                props.onBarSelected(bar[0].datasetIndex);
                            }
                        }
                    }
                }
            />
        </div>
    );
};

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function GraphLevels(i18n: i18n) {
    const days = i18n.t("Glossary.Days").toLowerCase();
    return [
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Knowledge,
            options: [
                { key: 0, text: i18n.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange0") },
                { key: 1, text: "0-24%" },
                { key: 2, text: "25-49%" },
                { key: 3, text: "50-74%" },
                { key: 4, text: "75-99%" },
                { key: 5, text: "100%" }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Result,
            options: [
                { key: 0, text: "0-24%" },
                { key: 1, text: "25-49%" },
                { key: 2, text: "50-74%" },
                { key: 3, text: "75-100%" }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Completed,
            options: [
                { key: 0, text: "0-24%" },
                { key: 1, text: "25-49%" },
                { key: 2, text: "50-74%" },
                { key: 3, text: "75-99%" },
                { key: 4, text: "100%" }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Revision,
            options: [
                { key: 0, text: i18n.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange0") },
                { key: 1, text: i18n.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange1") },
                { key: 2, text: i18n.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange2") },
                { key: 3, text: i18n.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange3") },
                { key: 4, text: i18n.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.RevisionRange4") }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Trust,
            options: [
                { key: 0, text: "0-24%" },
                { key: 1, text: "25-49%" },
                { key: 2, text: "50-74%" },
                { key: 3, text: "75-100%" }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Engagement,
            options: [
                { key: 0, text: i18n.t("Views.Dashboards.Components.Commons.KnowledgeTestGraph.NeverAnswered").split('\n').join(" ") },
                { key: 1, text: i18n.t(`${i18n.t("Common.Literals.MoreThan")} 30 ${days}`) },
                { key: 2, text: i18n.t(`30-15 ${days}`) },
                { key: 3, text: i18n.t(`14-8 ${days}`) },
                { key: 4, text: i18n.t(`Views.Dashboards.Common.LastWeek`) },
                { key: 5, text: i18n.t(`Views.Dashboards.Common.AllAnswered`) }
            ]
        }
    ];
}
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type KnowledgeListOptionsType = {
    column: DashboardKnowledgeIndicatorsSummaryColumn;
    options: {
        key: DashboardKnowledgeIndicatorsDetailColumn;
        text: string;
        group: "" | "users" | "topics" | "usersAndTopics" | "singleUser" | "anyTopic" | "anyUser" | "noUserOrNoTopic";
        useLevel: boolean;
        graph?: "all" | "user" | "topic";
    }[];
};

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
